import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import type { ConversationContext } from '@/services/conversation';

interface SJChatGreetingProps {
  context: ConversationContext;
}

export function SJChatGreeting({ context }: SJChatGreetingProps) {
  const greeting = buildGreeting(context);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center text-center py-8 md:py-12"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--sj-surface-muted)] mb-4">
        <Sparkles className="h-5 w-5 text-[var(--sj-text-secondary)]" />
      </div>

      <h1 className="text-xl font-semibold tracking-[-0.02em] text-[var(--sj-text-primary)] md:text-2xl">
        {greeting.title}
      </h1>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-[var(--sj-text-secondary)]">
        {greeting.subtitle}
      </p>
    </motion.div>
  );
}

function buildGreeting(context: ConversationContext): { title: string; subtitle: string } {
  if (context.industry) {
    return {
      title: `Let's explore AI in ${context.industry}`,
      subtitle: `I understand the unique challenges and opportunities in your industry. Tell me about a specific business problem, and I will guide you toward practical solutions.`,
    };
  }

  if (context.goal) {
    return {
      title: `Let's work on this together`,
      subtitle: `I can help you explore strategies, identify implementation approaches, and recommend specific workflows to achieve your business goal.`,
    };
  }

  if (context.article) {
    return {
      title: `Questions about what you've read?`,
      subtitle: `I have full context on this article. Ask me anything about applying these concepts to your specific business situation.`,
    };
  }

  return {
    title: `How can I help your business?`,
    subtitle: `Describe your industry, a challenge you're facing, or a goal you want to achieve. I'll provide practical enterprise guidance grounded in real knowledge.`,
  };
}
